import { createContext, useMemo } from 'react';
import { useCampaignLoader } from '../../hooks/useCampaignLoader';

export const CampaignContext = createContext(null);

/**
 * Campaign Context Provider
 * Provides campaign state and utility functions across the app
 */
export const CampaignProvider = ({ children }) => {
  const { campaign, isLoading, error } = useCampaignLoader();

  // Check if campaign is active
  const isCampaignActive = useMemo(() => {
    return !!campaign;
  }, [campaign]);

  /**
   * Check if service has a discount (without conditions)
   * @param {string} serviceId - Service identifier
   * @returns {boolean}
   */
  const hasServiceDiscount = useMemo(
    () => (serviceId) => {
      if (!campaign || !campaign.discounts || !serviceId) {
        return false;
      }

      return !!campaign.discounts[serviceId];
    },
    [campaign]
  );

  /**
   * Get discount percent for a service
   * @param {string} serviceId - Service identifier
   * @returns {number}
   */
  const getDiscountPercent = useMemo(
    () => (serviceId) => {
      if (!campaign || !campaign.discounts || !serviceId) {
        return 0;
      }

      const serviceDiscount = campaign.discounts[serviceId];
      return serviceDiscount ? serviceDiscount.percent : 0;
    },
    [campaign]
  );

  /**
   * Check if discount applies to a specific service
   * @param {string} serviceId - Service identifier
   * @param {number} squareMeters - Square meters (optional)
   * @returns {boolean}
   */
  const appliesDiscount = useMemo(
    () => (serviceId, squareMeters = null) => {
      if (!campaign || !campaign.discounts || !serviceId) {
        return false;
      }

      const serviceDiscount = campaign.discounts[serviceId];

      if (!serviceDiscount) {
        return false;
      }

      // Check minSquareMeters condition
      if (serviceDiscount.minSquareMeters) {
        if (!squareMeters || squareMeters < serviceDiscount.minSquareMeters) {
          return false;
        }
      }

      return true;
    },
    [campaign]
  );

  /**
   * Calculate discount for a service
   * @param {number} price - Base price
   * @param {string} serviceId - Service identifier
   * @param {number} squareMeters - Square meters (optional)
   * @returns {Object} { discountedPrice, hasDiscount, discountPercent }
   */
  const calculateDiscount = useMemo(
    () => (price, serviceId, squareMeters = null) => {
      if (!appliesDiscount(serviceId, squareMeters)) {
        return {
          discountedPrice: price,
          hasDiscount: false,
          discountPercent: 0,
        };
      }

      const serviceDiscount = campaign.discounts[serviceId];
      const discountPercent = serviceDiscount.percent;
      const discountedPrice = price * (1 - discountPercent / 100);

      return {
        discountedPrice,
        hasDiscount: true,
        discountPercent,
      };
    },
    [campaign, appliesDiscount]
  );

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      campaign,
      isLoading,
      error,
      isCampaignActive,
      hasServiceDiscount,
      getDiscountPercent,
      appliesDiscount,
      calculateDiscount,
    }),
    [
      campaign,
      isLoading,
      error,
      isCampaignActive,
      hasServiceDiscount,
      getDiscountPercent,
      appliesDiscount,
      calculateDiscount,
    ]
  );

  return <CampaignContext.Provider value={contextValue}>{children}</CampaignContext.Provider>;
};
